import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Initial search state
const initialSearchState = {
  query: '',
  results: [],
  selectedResult: null,
  isSearching: false,
  searchHistory: [],
  isVisible: false
};

// Create Zustand SearchStore with persist middleware
export const useSearchStore = create(
  persist(
    (set, get) => ({
      // State
      ...initialSearchState,

      // Actions
      setQuery: (query) => {
        set({ query });
      },

      setResults: (results) => {
        set({ results });
      },

      setSelectedResult: (result) => {
        set({ selectedResult: result });
      },

      setSearching: (isSearching) => {
        set({ isSearching });
      },

      setVisible: (isVisible) => {
        set({ isVisible });
      },

      // Add to search history (max 10 recent searches)
      addToHistory: (query) => {
        if (!query || query.length < 2) return;

        const state = get();
        const history = state.searchHistory.filter(item => item !== query);
        const newHistory = [query, ...history].slice(0, 10);

        set({ searchHistory: newHistory });
      },

      // Clear search history
      clearHistory: () => {
        set({ searchHistory: [] });
      },

      // Reset all search state
      resetSearch: () => {
        set({
          ...initialSearchState,
          searchHistory: get().searchHistory // Preserve history
        });
      },

      // Get search state summary
      getSearchState: () => {
        const state = get();
        return {
          query: state.query,
          results: state.results,
          selectedResult: state.selectedResult,
          isSearching: state.isSearching,
          isVisible: state.isVisible,
          hasResults: state.results.length > 0,
          resultCount: state.results.length
        };
      },

      // Execute search action (to be called by searchService)
      executeSearch: async (searchFunction) => {
        const state = get();

        if (!state.query || state.query.length < 2) {
          set({ results: [], selectedResult: null });
          return;
        }

        set({ isSearching: true });

        try {
          const results = await searchFunction(state.query);
          set({
            results: results || [],
            isSearching: false
          });

          // Add to history if we got results
          if (results && results.length > 0) {
            get().addToHistory(state.query);
          }
        } catch (error) {
          console.error('Search execution failed:', error);
          set({
            results: [],
            isSearching: false,
            selectedResult: null
          });
        }
      }
    }),
    {
      name: 'search-storage',
      version: 1,
      // Only persist search history and preferences, not active search state
      partialize: (state) => ({
        searchHistory: state.searchHistory
      })
    }
  )
);

// Helper hooks for common use cases
export const useSearchQuery = () => useSearchStore(state => state.query);
export const useSearchResults = () => useSearchStore(state => state.results);
export const useSearchState = () => useSearchStore(state => state.getSearchState());
export const useIsSearching = () => useSearchStore(state => state.isSearching);